import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "@phosphor-icons/react";
import { useLenis } from "lenis/react";
import { useLang } from "@/LanguageContext";
import { IMAGES } from "@/content";

const ease = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  const container = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } } };
  const line = { hidden: { y: "115%" }, show: { y: 0, transition: { duration: 1.05, ease } } };

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) lenis ? lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="Brussels European quarter" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-navy via-navy/70 to-navy/40" />
      <div className="absolute inset-0 z-10 bg-navy/35" />
      <motion.div style={{ y: overlayY }} className="absolute -top-40 -right-40 w-[45rem] h-[45rem] glow-gold z-10 rounded-full blur-3xl" />

      <div className="relative z-20 mx-auto max-w-[1400px] w-full px-6 lg:px-12 pb-20 lg:pb-28 pt-32">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}>
          <span className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.4em] text-gold">
            <span className="h-px w-10 bg-gold/60" />
            {t.hero.eyebrow}
          </span>
        </motion.div>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-serif text-white mt-6 text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-[5.2rem] lg:leading-[0.98] max-w-5xl tracking-[-0.02em]"
        >
          {t.hero.titleLines.map((l, i) => (
            <span key={i} className="mask-line">
              <motion.span variants={line} className={`block ${i === t.hero.titleLines.length - 1 ? "text-gold italic" : ""}`}>
                {l}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => go("contact")}
            className="btn-gold inline-flex items-center gap-2 bg-gold text-navy font-semibold rounded-full pl-7 pr-5 py-4"
          >
            <span>{t.hero.ctaPrimary}</span>
            <ArrowUpRight weight="bold" size={18} className="relative z-10" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => go("expertise")}
            className="inline-flex items-center gap-2 text-white border border-white/25 rounded-full px-7 py-4 hover:border-gold hover:text-gold transition-colors duration-300"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-14 border-t border-white/10 pt-6 max-w-2xl"
        >
          <span className="text-[11px] sm:text-xs uppercase tracking-[0.28em] text-white/55">{t.hero.groupLine}</span>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-8 z-20 hidden lg:flex flex-col items-center gap-2 text-white/50"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] [writing-mode:vertical-lr]">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
}
